<template>
	<div class="br-tab-products">
		<section>
						<KpiCards class="mb-3" :items="kpiItems" :loading="summary.loading" :skeleton="5" />

<!-- ============ MAIN REPORT PANEL ============ -->
			<v-card class="br-panel mb-3" flat>
				<v-card-text class="pb-2 pt-3">
					<!-- Panel header: title + tabs -->
					<div class="d-flex align-center flex-wrap br-panel-header">
						<span class="br-accent-bar" />
						<h1 class="br-panel-title">{{ tt("product_sales_report") }}</h1>
						<span class="br-panel-count">
							{{ activeView === "product" ? total : (dayTotal || dayItems.length) }} {{ tt("records") }}
						</span>
						<v-spacer />
						<div class="br-tabs">
							<div
								class="br-tab"
								:class="{ 'br-tab--active': activeView === 'product' }"
								@click="switchView('product')"
							>
								<v-icon size="13" class="mr-1" :color="activeView === 'product' ? 'primary' : 'grey'">fal fa-boxes</v-icon>
								{{ tt("by_product") }}
							</div>
							<div
								class="br-tab"
								:class="{ 'br-tab--active': activeView === 'day' }"
								@click="switchView('day')"
							>
								<v-icon size="13" class="mr-1" :color="activeView === 'day' ? 'primary' : 'grey'">fal fa-calendar-alt</v-icon>
								{{ tt("by_day") }}
							</div>
						</div>
					</div>

					<!-- Filter row -->
					<div class="d-flex align-center flex-wrap br-filter-row mt-3">
						<TextSearch
							v-model="search"
							class="br-search"
							placeholder="search_name_or_barcode"
							@search="onSearch"
							@onClear="onClearSearch"
						/>

						<div class="d-flex align-center br-date-range">
							<DatePicker
								:dense="true"
								:placeholder="false"
								label="start_date"
								v-model="start_date"
								hide-details
								style="max-width: 152px"
							/>
							<span class="br-date-arrow mx-1">→</span>
							<DatePicker
								:dense="true"
								:placeholder="false"
								label="end_date"
								v-model="end_date"
								hide-details
								style="max-width: 152px"
							/>
						</div>

						<TextSelect
							v-model="category"
							label="category"
							:items="categoryOptions"
							text="text"
							itemValue="value"
							:returnObject="false"
							dense
							:placeholder="false"
							hide-details
							:disabled="categoryOptionsError"
							class="br-select"
						/>

						<TextSelect
							v-model="sort"
							label="sort_by"
							:items="sortOptions"
							text="label"
							itemValue="value"
							:returnObject="false"
							dense
							:placeholder="false"
							hide-details
							:disabled="activeView === 'day'"
							class="br-select"
						/>

						<span v-if="isFiltered" class="br-clear-link" @click="clearFilter">
							{{ tt("clear_all_filters") }}
						</span>

						<v-spacer />

						<div
							class="br-export-btn"
							:class="{ 'opacity-60': exporting }"
							@click="!exporting && exportReport()"
						>
							<v-progress-circular
								v-if="exporting"
								size="13"
								width="2"
								indeterminate
								color="primary"
								class="mr-1"
							/>
							<v-icon v-else size="13" class="mr-1">fal fa-file-export</v-icon>
							{{ tt("excel") }}
						</div>
					</div>

					<!-- Quick range pills -->
					<div class="d-flex align-center flex-wrap gap-2 mt-2 pt-1 br-quick-bar">
						<span class="text-caption grey--text mr-2">
							<v-icon size="12" class="mr-1">fal fa-clock</v-icon>
							{{ tt("quick_range") }}:
						</span>
						<button
							v-for="range in quickRanges"
							:key="range.value"
							type="button"
							class="br-quick-chip"
							:class="{ 'br-quick-chip--active': activeRange === range.value }"
							@click="applyQuickRange(range.value)"
						>
							{{ range.label }}
						</button>
					</div>

					<v-divider class="my-3" />

		<!-- ============ DATA TABLE CARD — BY PRODUCT ============ -->
		<div class="pr-table-card" v-if="activeView === 'product'">
			<div class="pr-table-head">
				<div class="pr-table-head-left">
					<h3 class="pr-table-title">{{ tt("product_sales_matrix") }}</h3>
					<span class="pr-range-pill">{{ rangeLabel }}</span>
				</div>
				<span class="pr-table-hint">
					<span class="pr-hint-dot" />
					{{ tt("click_row_daily_breakdown") }}
				</span>
			</div>

			<!-- Inline, non-blocking error. Never a global modal, filters stay usable. -->
			<v-alert v-if="error" type="error" text class="mx-4 mt-2 d-flex align-center flex-wrap">
				<span class="mr-3">{{ tt("unable_to_load_product_report") }}</span>
				<v-btn small outlined color="error" @click="fetchItem">
					<v-icon left small>fal fa-redo</v-icon>
					{{ tt("retry") }}
				</v-btn>
			</v-alert>

			<div v-if="loading" class="pr-skeleton-wrap">
				<v-skeleton-loader type="table-row-divider@6" />
			</div>

			<div v-else-if="!error && !items.length" class="pr-empty">
				<div class="pr-empty-icon">📦</div>
				<h4 class="pr-empty-title">{{ tt("no_matching_product_sales") }}</h4>
				<p class="pr-empty-text">
					{{ tt("no_matching_product_sales_desc") }}
				</p>
				<button type="button" class="pr-empty-btn" @click="clearFilter">{{ tt("clear_all_filters") }}</button>
			</div>

			<div v-if="!error && !loading && items.length" class="pr-table-scroll">
				<table class="pr-table">
					<thead>
						<tr>
							<th class="pr-th-icon"></th>
							<th>{{ tt("product_details") }}</th>
							<th>{{ tt("category") }}</th>
							<th>{{ tt("unit") }}</th>
							<th class="pr-th-num">{{ tt("qty_sold") }}</th>
							<th class="pr-th-num">{{ tt("avg_per_day") }}</th>
							<th class="pr-th-num">{{ tt("revenue") }}</th>
							<th class="pr-th-num">{{ tt("days_sold") }}</th>
							<th class="pr-th-num">{{ tt("last_sale") }}</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="item in sortedItems">
							<tr
								:key="item.id"
								class="pr-row"
								:class="{ 'pr-row-open': expanded.has(item.id) }"
								@click="toggleExpand(item)"
							>
								<td class="pr-th-icon">
									<span class="pr-chevron" :class="{ 'pr-chevron-open': expanded.has(item.id) }">
										<v-icon size="14">fal fa-chevron-right</v-icon>
									</span>
								</td>
								<td>
									<div class="pr-prod-cell">
										<div class="pr-avatar">
											<img
												:src="productImageUrl(item)"
												:alt="item.name || ''"
												class="pr-avatar-img"
												@error="onImgError(item, $event)"
											/>
										</div>
										<div>
											<div class="pr-prod-name">{{ item.name || "—" }}</div>
											<div class="pr-prod-sub">
												<code class="pr-code-chip">{{ hasRealValue(item, "code") ? item.code : "—" }}</code>
											</div>
										</div>
									</div>
								</td>
								<td><span class="pr-muted-chip">{{ categoryLabel(item) }}</span></td>
								<td><span class="pr-muted-chip">{{ unitLabel(item) }}</span></td>
								<td class="pr-th-num">
									<div class="pr-qty-value">
										{{ hasRealValue(item, "quantity_sold") ? formatNumber(item.quantity_sold) : "—" }}
									</div>
									<div v-if="hasRealValue(item, 'quantity_sold')" class="pr-qty-bar">
										<div class="pr-qty-bar-fill" :style="{ width: qtyPct(item) + '%' }" />
									</div>
								</td>
								<td class="pr-th-num" :class="{ 'pr-muted': item.avg_per_day === null || item.avg_per_day === undefined }">
									{{ item.avg_per_day === null || item.avg_per_day === undefined ? "—" : formatNumber(item.avg_per_day) }}
								</td>
								<td class="pr-th-num" :class="{ 'pr-muted': !hasRealValue(item, 'revenue') }">
									{{ hasRealValue(item, "revenue") ? formatNumber(item.revenue) : "—" }}
								</td>
								<td class="pr-th-num" :class="{ 'pr-muted': !hasRealValue(item, 'days_sold') }">
									{{ hasRealValue(item, "days_sold") ? formatNumber(item.days_sold) : "—" }}
								</td>
								<td class="pr-th-num" :class="{ 'pr-muted': !item.last_sale }">
									{{ formatDate(item.last_sale) }}
								</td>
							</tr>
							<tr v-if="expanded.has(item.id)" :key="item.id + '-detail'" class="pr-detail-row">
								<td colspan="9">
									<div class="pr-detail-inner" v-if="!dailyCache[item.id] || dailyCache[item.id].loading" @click.stop>
										<v-progress-circular indeterminate size="14" width="2" color="#94a3b8" class="mr-2" />
										{{ tt("loading_daily_breakdown") }}
									</div>
									<div class="pr-detail-inner" v-else-if="dailyCache[item.id].error" @click.stop>
										<v-icon size="14" color="#dc2626" class="mr-2">fal fa-exclamation-circle</v-icon>
										{{ tt("could_not_load_daily_breakdown") }}
										<v-btn x-small outlined color="error" class="ml-2" @click="retryDaily(item)">{{ tt("retry") }}</v-btn>
									</div>
									<div class="pr-detail-inner" v-else-if="!dailyCache[item.id].days.length" @click.stop>
										<v-icon size="14" color="#94a3b8" class="mr-2">fal fa-info-circle</v-icon>
										{{ tt("no_daily_sales_recorded") }}
									</div>
									<div v-else class="pr-detail-body" @click.stop>
										<div class="pr-timeline-header">
											<span class="pr-timeline-dot" />
											{{ tt("daily_sales_timeline") }} • {{ (item.name || "").toUpperCase() }}
										</div>
										<div class="pr-timeline-row">
											<div v-for="d in dailyCache[item.id].days" :key="d.date" class="pr-timeline-card">
												<div class="pr-timeline-date">{{ dailyCardLabel(d.date) }}</div>
												<div class="pr-timeline-qty">
													{{ formatNumber(d.quantity_sold) }}<span v-if="unitLabel(item) !== '—'"> {{ unitLabel(item) }}</span>
												</div>
												<div class="pr-timeline-revenue">{{ formatNumber(d.revenue) }} ₭</div>
											</div>
										</div>
									</div>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>

			<div v-if="!error && !loading && items.length" class="pr-table-foot">
				<div class="pr-foot-meta">
					{{ tt("showing") }} <b>{{ items.length }}</b> {{ tt("of") }} <b>{{ total }}</b> {{ tt("products_on_this_page") }}
				</div>
				<div class="pr-pager">
					<span class="pr-pager-label">{{ tt("rows_per_page") }}:</span>
					<v-select
						v-model="perPage"
						:items="[10, 15, 25, 50]"
						dense
						outlined
						hide-details
						class="pr-per-page"
						@change="onPerPageChange"
					/>
					<span class="pr-page-info">{{ tt("page") }} {{ currentPage }} {{ tt("of") }} {{ lastPage }}</span>
					<button
						type="button"
						class="pr-pager-btn"
						:disabled="loading || currentPage <= 1"
						@click="goToPage(currentPage - 1)"
					>
						<v-icon size="14">fal fa-chevron-left</v-icon>
					</button>
					<button
						type="button"
						class="pr-pager-btn"
						:disabled="loading || currentPage >= lastPage"
						@click="goToPage(currentPage + 1)"
					>
						<v-icon size="14">fal fa-chevron-right</v-icon>
					</button>
				</div>
			</div>
		</div>

		<!-- ============ DATA TABLE CARD — BY DAY ============ -->
		<div class="pr-table-card" v-else>
			<div class="pr-table-head">
				<div class="pr-table-head-left">
					<h3 class="pr-table-title">{{ tt("daily_sales_breakdown") }}</h3>
					<span class="pr-range-pill">{{ rangeLabel }}</span>
				</div>
				<span class="pr-table-hint">
					<span class="pr-hint-dot" />
					{{ tt("one_row_per_day") }}
				</span>
			</div>

			<!-- Isolated from the By Product table's error/loading state on purpose. -->
			<v-alert v-if="dayError" type="error" text class="mx-4 mt-2 d-flex align-center flex-wrap">
				<span class="mr-3">{{ tt("unable_to_load_daily_sales") }}</span>
				<v-btn small outlined color="error" @click="fetchDayItems(dayCurrentPage)">
					<v-icon left small>fal fa-redo</v-icon>
					{{ tt("retry") }}
				</v-btn>
			</v-alert>

			<div v-if="dayLoading" class="pr-skeleton-wrap">
				<v-skeleton-loader type="table-row-divider@6" />
			</div>

			<div v-else-if="!dayError && !dayItems.length" class="pr-empty">
				<div class="pr-empty-icon">📅</div>
				<h4 class="pr-empty-title">{{ tt("no_matching_daily_sales") }}</h4>
				<p class="pr-empty-text">
					{{ tt("no_matching_product_sales_desc") }}
				</p>
				<button type="button" class="pr-empty-btn" @click="clearFilter">{{ tt("clear_all_filters") }}</button>
			</div>

			<div v-if="!dayError && !dayLoading && dayItems.length" class="pr-table-scroll">
				<table class="pr-table">
					<thead>
						<tr>
							<th class="pr-th-icon"></th>
							<th>{{ tt("sales_date") }}</th>
							<th class="pr-th-num">{{ tt("active_skus_sold") }}</th>
							<th class="pr-th-num">{{ tt("total_qty_sold_col") }}</th>
							<th class="pr-th-num">{{ tt("avg_qty_per_sku") }}</th>
							<th class="pr-th-num">{{ tt("total_revenue_col") }}</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="row in dayItems">
							<tr
								:key="row.date"
								class="pr-row"
								:class="{ 'pr-row-open': dayExpanded.has(row.date) }"
								@click="toggleDayExpand(row)"
							>
								<td class="pr-th-icon">
									<span class="pr-chevron" :class="{ 'pr-chevron-open': dayExpanded.has(row.date) }">
										<v-icon size="14">fal fa-chevron-right</v-icon>
									</span>
								</td>
								<td>
									<div class="pr-date-cell">
										<span class="pr-date-main">{{ formatDayDate(row.date) }}</span>
										<span class="pr-date-weekday">{{ dayWeekdayName(row.date) }}</span>
									</div>
								</td>
								<td class="pr-th-num">{{ formatNumber(row.active_skus) }} {{ tt("products") }}</td>
								<td class="pr-th-num">
									<div class="pr-qty-value">{{ formatNumber(row.total_quantity_sold) }}</div>
									<div class="pr-qty-bar">
										<div class="pr-qty-bar-fill" :style="{ width: dayQtyPct(row) + '%' }" />
									</div>
								</td>
								<td class="pr-th-num">{{ formatNumber(row.avg_quantity_per_sku) }}</td>
								<td class="pr-th-num pr-revenue-green">{{ formatNumber(row.total_revenue) }} ₭</td>
							</tr>
							<tr v-if="dayExpanded.has(row.date)" :key="row.date + '-detail'" class="pr-detail-row">
								<td colspan="6">
									<div
										class="pr-detail-inner"
										v-if="!dayProductsCache[row.date] || dayProductsCache[row.date].loading"
										@click.stop
									>
										<v-progress-circular indeterminate size="14" width="2" color="#94a3b8" class="mr-2" />
										{{ tt("loading_daily_breakdown") }}
									</div>
									<div class="pr-detail-inner" v-else-if="dayProductsCache[row.date].error" @click.stop>
										<v-icon size="14" color="#dc2626" class="mr-2">fal fa-exclamation-circle</v-icon>
										{{ tt("could_not_load_daily_breakdown") }}
										<v-btn x-small outlined color="error" class="ml-2" @click="retryDayProducts(row)">{{
											tt("retry")
										}}</v-btn>
									</div>
									<div
										class="pr-detail-inner"
										v-else-if="!dayProductsCache[row.date].products.length"
										@click.stop
									>
										<v-icon size="14" color="#94a3b8" class="mr-2">fal fa-info-circle</v-icon>
										{{ tt("no_daily_sales_recorded") }}
									</div>
									<div v-else class="pr-detail-body" @click.stop>
										<div class="pr-timeline-header">
											<span class="pr-timeline-dot" />
											{{ tt("products_sold_on") }} {{ formatDayDate(row.date) }} ({{
												dayProductsCache[row.date].products.length
											}}
											{{ tt("items") }})
										</div>
										<table class="pr-byday-table">
											<thead>
												<tr>
													<th>{{ tt("product_name_col") }}</th>
													<th>{{ tt("code") }}</th>
													<th>{{ tt("unit") }}</th>
													<th class="pr-th-num">{{ tt("qty") }}</th>
													<th class="pr-th-num">{{ tt("revenue") }}</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="p in dayProductsCache[row.date].products" :key="(p.code || '') + p.name">
													<td>{{ p.name || "—" }}</td>
													<td>{{ p.code || "—" }}</td>
													<td>{{ p.unit || "—" }}</td>
													<td class="pr-th-num">{{ formatNumber(p.quantity_sold) }}</td>
													<td class="pr-th-num pr-revenue-green">{{ formatNumber(p.revenue) }} ₭</td>
												</tr>
											</tbody>
										</table>
									</div>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>

			<div v-if="!dayError && !dayLoading && dayItems.length" class="pr-table-foot">
				<div class="pr-foot-meta">
					{{ tt("showing") }} <b>{{ dayItems.length }}</b> {{ tt("of") }} <b>{{ dayTotal }}</b> {{ tt("days_on_this_page") }}
				</div>
				<div class="pr-pager">
					<span class="pr-pager-label">{{ tt("rows_per_page") }}:</span>
					<v-select
						v-model="dayPerPage"
						:items="[10, 15, 25, 50]"
						dense
						outlined
						hide-details
						class="pr-per-page"
						@change="onDayPerPageChange"
					/>
					<span class="pr-page-info">{{ tt("page") }} {{ dayCurrentPage }} {{ tt("of") }} {{ dayLastPage }}</span>
					<button
						type="button"
						class="pr-pager-btn"
						:disabled="dayLoading || dayCurrentPage <= 1"
						@click="goToDayPage(dayCurrentPage - 1)"
					>
						<v-icon size="14">fal fa-chevron-left</v-icon>
					</button>
					<button
						type="button"
						class="pr-pager-btn"
						:disabled="dayLoading || dayCurrentPage >= dayLastPage"
						@click="goToDayPage(dayCurrentPage + 1)"
					>
						<v-icon size="14">fal fa-chevron-right</v-icon>
					</button>
				</div>
			</div>
		</div>
		</v-card-text>
		</v-card>

		<!-- Non-blocking export failure notice — the global axios "Failed"
		     modal is skipped for this call (skipErrorAlert) so a failed
		     export never interrupts the rest of the page; this is the only
		     feedback shown. -->
		<v-snackbar v-model="exportSnackbar" color="error" timeout="5000" bottom>
			{{ exportSnackbarText }}
			<template v-slot:action="{ attrs }">
				<v-btn text v-bind="attrs" @click="exportSnackbar = false">{{ tt("dismiss") }}</v-btn>
			</template>
		</v-snackbar>
		</section>
	</div>
</template>

<script>
import TextSearch from "@/components/global/TextSearch";
import TextSelect from "@/components/global/TextSelect";

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
	.toISOString()
	.substr(0, 10);

const addDaysIso = (isoDate, n) => {
	const d = new Date(isoDate + "T00:00:00");
	d.setDate(d.getDate() + n);
	return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
		.toISOString()
		.substr(0, 10);
};

const last7Start = addDaysIso(today, -6);

// Which product-model attributes we've already warned about being missing
// from the API response this session, so a paginated table doesn't spam the
// console with the same warning for every row on every page load.
const warnedMissingFields = new Set();

// Fallback translations for keys that are new to this page and may not yet
// exist in the project's real locale files — see tt() in methods for why
// this exists and when it stops being used. Locale codes match this
// project's existing convention: "en", "th", "la" (Lao), "zh" (Chinese).
const TT_FALLBACK = {
	qty_sold_high_to_low: {
		en: "Qty sold (High → Low)",
		th: "จำนวนขาย (มาก → น้อย)",
		la: "ຈຳນວນຂາຍ (ຫຼາຍ → ໜ້ອຍ)",
		zh: "销量（高 → 低）",
	},
	qty_sold_low_to_high: {
		en: "Qty sold (Low → High)",
		th: "จำนวนขาย (น้อย → มาก)",
		la: "ຈຳນວນຂາຍ (ໜ້ອຍ → ຫຼາຍ)",
		zh: "销量（低 → 高）",
	},
	revenue_high_to_low: {
		en: "Revenue (High → Low)",
		th: "รายได้ (มาก → น้อย)",
		la: "ລາຍຮັບ (ຫຼາຍ → ໜ້ອຍ)",
		zh: "收入（高 → 低）",
	},
	product_name_a_to_z: {
		en: "Product name (A → Z)",
		th: "ชื่อสินค้า (ก → ฮ)",
		la: "ຊື່ສິນຄ້າ (ກ → ຮ)",
		zh: "产品名称（A → Z）",
	},
	all_units: {
		en: "All units",
		th: "หน่วยทั้งหมด",
		la: "ໜ່ວຍທັງໝົດ",
		zh: "所有单位",
	},

	// The rest of these were added for the "no visible underscores anywhere
	// on the page" fix. Same mechanism as above: this is used only when the
	// real $t(key) comes back unchanged (key missing from the project's
	// actual locale files) — the instant those files gain these keys, tt()
	// starts returning the real translation and this table stops being
	// reached for that key. Nothing here renames or touches the i18n key
	// itself, only what's shown when it can't be resolved yet.
	product_sales_report: {
		en: "Product Sales Report",
		th: "รายงานการขายสินค้า",
		la: "ລາຍງານການຂາຍສິນຄ້າ",
		zh: "产品销售报告",
	},
	product_sales_report_subtitle: {
		en: "Track quantity sold, revenue, and product performance over any date range.",
		th: "ติดตามจำนวนที่ขาย รายได้ และประสิทธิภาพของสินค้าในช่วงวันที่ที่เลือก",
		la: "ຕິດຕາມຈຳນວນທີ່ຂາຍ, ລາຍຮັບ, ແລະປະສິດທິພາບຂອງສິນຄ້າໃນຊ່ວງວັນທີທີ່ເລືອກ",
		zh: "跟踪所选日期范围内的销量、收入和产品表现",
	},
	by_product: { en: "By Product", th: "ตามสินค้า", la: "ຕາມສິນຄ້າ", zh: "按产品" },
	by_day: { en: "By Day", th: "ตามวัน", la: "ຕາມວັນ", zh: "按天" },
	total_qty_sold: { en: "Total Qty Sold", th: "จำนวนขายทั้งหมด", la: "ຈຳນວນຂາຍທັງໝົດ", zh: "总销量" },
	units: { en: "units", th: "หน่วย", la: "ໜ່ວຍ", zh: "单位" },
	days_in_period: { en: "days in period", th: "วันในช่วงเวลา", la: "ວັນໃນຊ່ວງເວລາ", zh: "期间天数" },
	not_available_right_now: {
		en: "Not available right now",
		th: "ไม่พร้อมใช้งานในขณะนี้",
		la: "ບໍ່ພ້ອມໃຊ້ງານໃນຕອນນີ້",
		zh: "暂时不可用",
	},
	on_loaded_rows: {
		en: "Across the full filtered range",
		th: "ครอบคลุมช่วงที่กรองทั้งหมด",
		la: "ຄອບຄຸມຂອບເຂດທີ່ກັ່ນຕອງທັງໝົດ",
		zh: "覆盖整个筛选范围",
	},
	active_products_sold: {
		en: "Active Products Sold",
		th: "สินค้าที่ขายได้",
		la: "ສິນຄ້າທີ່ຂາຍໄດ້",
		zh: "已售出的活跃产品",
	},
	skus_with_transactions: {
		en: "SKUs with transactions",
		th: "SKU ที่มีการทำรายการ",
		la: "SKU ທີ່ມີການເຮັດທຸລະກຳ",
		zh: "有交易记录的SKU",
	},
	of_full_catalog: { en: "Of full catalog", th: "จากแคตตาล็อกทั้งหมด", la: "ຈາກແຄຕາລັອກທັງໝົດ", zh: "占全部目录" },
	total_gross_revenue: {
		en: "Total Gross Revenue",
		th: "รายได้รวมทั้งหมด",
		la: "ລາຍຮັບລວມທັງໝົດ",
		zh: "总收入",
	},
	from_completed_credit_sales: {
		en: "From completed & credit sales",
		th: "จากการขายที่เสร็จสมบูรณ์และการขายเชื่อ",
		la: "ຈາກການຂາຍທີ່ສຳເລັດແລະການຂາຍເຊື່ອ",
		zh: "来自已完成及赊销交易",
	},
	pre_discount_sales_value: {
		en: "Pre-discount sales value",
		th: "มูลค่าการขายก่อนหักส่วนลด",
		la: "ມູນຄ່າການຂາຍກ່ອນຫັກສ່ວນຫຼຸດ",
		zh: "折扣前销售额",
	},
	avg_qty_per_day: { en: "Avg Qty / Day", th: "จำนวนเฉลี่ย/วัน", la: "ຈຳນວນສະເລ່ຍ/ວັນ", zh: "日均数量" },
	units_per_day: { en: "units/day", th: "หน่วย/วัน", la: "ໜ່ວຍ/ວັນ", zh: "单位/天" },
	across_full_filtered_range: {
		en: "Across the full filtered range",
		th: "ครอบคลุมช่วงที่กรองทั้งหมด",
		la: "ຄອບຄຸມຂອບເຂດທີ່ກັ່ນຕອງທັງໝົດ",
		zh: "覆盖整个筛选范围",
	},
	velocity_index: { en: "Velocity Index", th: "ดัชนีความเร็วการขาย", la: "ດັດຊະນີຄວາມໄວການຂາຍ", zh: "销售速度指数" },
	top_seller_product: {
		en: "Top Seller Product",
		th: "สินค้าขายดีที่สุด",
		la: "ສິນຄ້າຂາຍດີທີ່ສຸດ",
		zh: "最畅销产品",
	},
	no_sales_in_selected_range: {
		en: "No sales in selected range",
		th: "ไม่มีการขายในช่วงที่เลือก",
		la: "ບໍ່ມີການຂາຍໃນຊ່ວງທີ່ເລືອກ",
		zh: "所选范围内无销售记录",
	},
	sold: { en: "sold", th: "ขาย", la: "ຂາຍ", zh: "已售" },
	revenue: { en: "Revenue", th: "รายได้", la: "ລາຍຮັບ", zh: "收入" },
	star_performer: { en: "Star Performer", th: "สินค้าดาวเด่น", la: "ສິນຄ້າດາວເດັ່ນ", zh: "明星产品" },
	filter_criteria: { en: "Filter Criteria", th: "เกณฑ์การกรอง", la: "ເງື່ອນໄຂການກັ່ນຕອງ", zh: "筛选条件" },
	filter_criteria_desc: {
		en: "Narrow the report by category, unit, date range, or search",
		th: "จำกัดรายงานตามหมวดหมู่ หน่วย ช่วงวันที่ หรือการค้นหา",
		la: "ຈຳກັດລາຍງານຕາມໝວດໝູ່, ໜ່ວຍ, ຊ່ວງວັນທີ ຫຼືການຄົ້ນຫາ",
		zh: "按类别、单位、日期范围或搜索缩小报告范围",
	},
	reset_filters: { en: "Reset Filters", th: "ล้างตัวกรอง", la: "ລ້າງຕົວກັ່ນຕອງ", zh: "重置筛选条件" },
	advanced: { en: "Advanced", th: "ขั้นสูง", la: "ຂັ້ນສູງ", zh: "高级" },
	advanced_filters_not_available_yet: {
		en: "Advanced filters not available yet",
		th: "ตัวกรองขั้นสูงยังไม่พร้อมใช้งาน",
		la: "ຕົວກັ່ນຕອງຂັ້ນສູງຍັງບໍ່ພ້ອມໃຊ້ງານ",
		zh: "高级筛选功能暂未开放",
	},
	category: { en: "Category", th: "หมวดหมู่", la: "ໝວດໝູ່", zh: "类别" },
	categories_could_not_be_loaded: {
		en: "Categories could not be loaded",
		th: "ไม่สามารถโหลดหมวดหมู่ได้",
		la: "ບໍ່ສາມາດໂຫລດໝວດໝູ່ໄດ້",
		zh: "无法加载类别",
	},
	packaging_unit: { en: "Packaging Unit", th: "หน่วยบรรจุภัณฑ์", la: "ໜ່ວຍບັນຈຸພັນ", zh: "包装单位" },
	units_could_not_be_loaded: {
		en: "Units could not be loaded",
		th: "ไม่สามารถโหลดหน่วยได้",
		la: "ບໍ່ສາມາດໂຫລດໜ່ວຍໄດ້",
		zh: "无法加载单位",
	},
	sort_by: { en: "Sort By", th: "เรียงตาม", la: "ຈັດລຳດັບຕາມ", zh: "排序方式" },
	start_date: { en: "Start Date", th: "วันที่เริ่มต้น", la: "ວັນທີເລີ່ມຕົ້ນ", zh: "开始日期" },
	end_date: { en: "End Date", th: "วันที่สิ้นสุด", la: "ວັນທີສິ້ນສຸດ", zh: "结束日期" },
	quick_search: { en: "Quick Search", th: "ค้นหาด่วน", la: "ຄົ້ນຫາໄວ", zh: "快速搜索" },
	search_name_or_barcode: {
		en: "Search name or barcode",
		th: "ค้นหาชื่อหรือบาร์โค้ด",
		la: "ຄົ້ນຫາຊື່ ຫຼືບາໂຄດ",
		zh: "搜索名称或条码",
	},
	export_not_available_yet: {
		en: "Export not available yet",
		th: "การส่งออกยังไม่พร้อมใช้งาน",
		la: "ການສົ່ງອອກຍັງບໍ່ພ້ອມໃຊ້ງານ",
		zh: "导出功能暂未开放",
	},
	// "Excel" is the product/format name, not translated business copy — kept
	// as-is across locales, same convention as other brand/format labels.
	excel: { en: "Excel", th: "Excel", la: "Excel", zh: "Excel" },
	unable_to_export_product_sales_report: {
		en: "Unable to export Product Sales Report.",
		th: "ไม่สามารถส่งออกรายงานยอดขายสินค้าได้",
		la: "ບໍ່ສາມາດສົ່ງອອກລາຍງານຍອດຂາຍສິນຄ້າໄດ້",
		zh: "无法导出产品销售报表。",
	},
	dismiss: { en: "Dismiss", th: "ปิด", la: "ປິດ", zh: "关闭" },
	quick_range: { en: "Quick Range", th: "ช่วงเวลาด่วน", la: "ຊ່ວງເວລາໄວ", zh: "快速范围" },
	today: { en: "Today", th: "วันนี้", la: "ມື້ນີ້", zh: "今天" },
	yesterday: { en: "Yesterday", th: "เมื่อวาน", la: "ມື້ວານນີ້", zh: "昨天" },
	last_7_days: { en: "Last 7 days", th: "7 วันล่าสุด", la: "7 ວັນຫຼ້າສຸດ", zh: "最近7天" },
	last_30_days: { en: "Last 30 days", th: "30 วันล่าสุด", la: "30 ວັນຫຼ້າສຸດ", zh: "最近30天" },
	this_month: { en: "This month", th: "เดือนนี้", la: "ເດືອນນີ້", zh: "本月" },
	all_categories: { en: "All categories", th: "ทุกหมวดหมู่", la: "ທຸກໝວດໝູ່", zh: "所有类别" },
	product_sales_matrix: {
		en: "Product Sales Matrix",
		th: "ตารางการขายสินค้า",
		la: "ຕາຕະລາງການຂາຍສິນຄ້າ",
		zh: "产品销售矩阵",
	},
	click_row_daily_breakdown: {
		en: "Click any row to view granular daily breakdown",
		th: "คลิกแถวใดก็ได้เพื่อดูรายละเอียดยอดขายรายวัน",
		la: "ກົດແຖວໃດກໍ່ໄດ້ເພື່ອເບິ່ງລາຍລະອຽດການຂາຍລາຍວັນ",
		zh: "点击任意行查看每日销售明细",
	},
	unable_to_load_product_report: {
		en: "Unable to load product report",
		th: "ไม่สามารถโหลดรายงานสินค้าได้",
		la: "ບໍ່ສາມາດໂຫລດລາຍງານສິນຄ້າໄດ້",
		zh: "无法加载产品报告",
	},
	retry: { en: "Retry", th: "ลองใหม่", la: "ລອງໃໝ່", zh: "重试" },
	no_matching_product_sales: {
		en: "No matching product sales",
		th: "ไม่พบยอดขายสินค้าที่ตรงกัน",
		la: "ບໍ່ພົບຍອດຂາຍສິນຄ້າທີ່ຕົງກັນ",
		zh: "没有匹配的产品销售记录",
	},
	no_matching_product_sales_desc: {
		en: "Try adjusting your filters or date range",
		th: "ลองปรับตัวกรองหรือช่วงวันที่",
		la: "ລອງປັບຕົວກັ່ນຕອງ ຫຼືຊ່ວງວັນທີ",
		zh: "请尝试调整筛选条件或日期范围",
	},
	clear_all_filters: { en: "Clear All Filters", th: "ล้างตัวกรองทั้งหมด", la: "ລ້າງຕົວກັ່ນຕອງທັງໝົດ", zh: "清除所有筛选条件" },
	product_details: { en: "Product Details", th: "รายละเอียดสินค้า", la: "ລາຍລະອຽດສິນຄ້າ", zh: "产品详情" },
	unit: { en: "Unit", th: "หน่วย", la: "ໜ່ວຍ", zh: "单位" },
	qty_sold: { en: "Qty Sold", th: "จำนวนที่ขาย", la: "ຈຳນວນທີ່ຂາຍ", zh: "销量" },
	avg_per_day: { en: "Avg / Day", th: "เฉลี่ย/วัน", la: "ສະເລ່ຍ/ວັນ", zh: "日均" },
	days_sold: { en: "Days Sold", th: "จำนวนวันที่ขาย", la: "ຈຳນວນວັນທີ່ຂາຍ", zh: "销售天数" },
	last_sale: { en: "Last Sale", th: "ขายล่าสุด", la: "ຂາຍຫຼ້າສຸດ", zh: "最近销售" },
	loading_daily_breakdown: {
		en: "Loading daily breakdown…",
		th: "กำลังโหลดรายละเอียดรายวัน…",
		la: "ກຳລັງໂຫລດລາຍລະອຽດລາຍວັນ…",
		zh: "正在加载每日明细…",
	},
	could_not_load_daily_breakdown: {
		en: "Could not load daily breakdown",
		th: "ไม่สามารถโหลดรายละเอียดรายวันได้",
		la: "ບໍ່ສາມາດໂຫລດລາຍລະອຽດລາຍວັນໄດ້",
		zh: "无法加载每日明细",
	},
	no_daily_sales_recorded: {
		en: "No daily sales recorded",
		th: "ไม่มีบันทึกยอดขายรายวัน",
		la: "ບໍ່ມີບັນທຶກຍອດຂາຍລາຍວັນ",
		zh: "没有每日销售记录",
	},
	date: { en: "Date", th: "วันที่", la: "ວັນທີ", zh: "日期" },
	showing: { en: "Showing", th: "แสดง", la: "ສະແດງ", zh: "显示" },
	of: { en: "of", th: "จาก", la: "ຈາກ", zh: "共" },
	products_on_this_page: { en: "products on this page", th: "สินค้าในหน้านี้", la: "ສິນຄ້າໃນໜ້ານີ້", zh: "此页产品" },
	rows_per_page: { en: "Rows per page", th: "แถวต่อหน้า", la: "ແຖວຕໍ່ໜ້າ", zh: "每页行数" },
	page: { en: "Page", th: "หน้า", la: "ໜ້າ", zh: "第" },
	daily_sales_breakdown: {
		en: "Daily Sales Breakdown",
		th: "รายละเอียดยอดขายรายวัน",
		la: "ລາຍລະອຽດຍອດຂາຍລາຍວັນ",
		zh: "每日销售明细",
	},
	one_row_per_day: {
		en: "One row per day in the selected range",
		th: "หนึ่งแถวต่อหนึ่งวันในช่วงที่เลือก",
		la: "ໜຶ່ງແຖວຕໍ່ໜຶ່ງວັນໃນຊ່ວງທີ່ເລືອກ",
		zh: "所选范围内每天一行",
	},
	unable_to_load_daily_sales: {
		en: "Unable to load daily sales",
		th: "ไม่สามารถโหลดยอดขายรายวันได้",
		la: "ບໍ່ສາມາດໂຫລດຍອດຂາຍລາຍວັນໄດ້",
		zh: "无法加载每日销售数据",
	},
	no_matching_daily_sales: {
		en: "No matching daily sales",
		th: "ไม่พบยอดขายรายวันที่ตรงกัน",
		la: "ບໍ່ພົບຍອດຂາຍລາຍວັນທີ່ຕົງກັນ",
		zh: "没有匹配的每日销售记录",
	},
	products_sold: { en: "Products Sold", th: "สินค้าที่ขายได้", la: "ສິນຄ້າທີ່ຂາຍໄດ້", zh: "已售产品" },
	days_on_this_page: { en: "days on this page", th: "วันในหน้านี้", la: "ວັນໃນໜ້ານີ້", zh: "此页天数" },
	daily_sales_timeline: {
		en: "Daily Sales Timeline",
		th: "รายการยอดขายรายวัน",
		la: "ລາຍການຍອດຂາຍລາຍວັນ",
		zh: "每日销售时间线",
	},

	// By Day view — Daily Sales Breakdown table + expanded per-date products.
	sales_date: { en: "Sales Date", th: "วันที่ขาย", la: "ວັນທີຂາຍ", zh: "销售日期" },
	active_skus_sold: { en: "Active SKUs Sold", th: "SKU ที่ขายได้", la: "SKU ທີ່ຂາຍໄດ້", zh: "已售SKU数" },
	total_qty_sold_col: { en: "Total Qty Sold", th: "จำนวนขายทั้งหมด", la: "ຈຳນວນຂາຍທັງໝົດ", zh: "总销量" },
	avg_qty_per_sku: { en: "Avg Qty / SKU", th: "จำนวนเฉลี่ย/SKU", la: "ຈຳນວນສະເລ່ຍ/SKU", zh: "件均数量" },
	total_revenue_col: { en: "Total Revenue", th: "รายได้รวม", la: "ລາຍຮັບລວມ", zh: "总收入" },
	products: { en: "products", th: "สินค้า", la: "ສິນຄ້າ", zh: "产品" },
	products_sold_on: { en: "Products sold on", th: "สินค้าที่ขายเมื่อ", la: "ສິນຄ້າທີ່ຂາຍໃນ", zh: "销售产品于" },
	items: { en: "items", th: "รายการ", la: "ລາຍການ", zh: "项" },
	product_name_col: { en: "Product Name", th: "ชื่อสินค้า", la: "ຊື່ສິນຄ້າ", zh: "产品名称" },
	qty: { en: "Qty", th: "จำนวน", la: "ຈຳນວນ", zh: "数量" },
	code: { en: "Code", th: "โค้ด", la: "ໂຄດ", zh: "代码" },
	sort_by_not_available_in_by_day: {
		en: "Sort By is not used in By Day — dates are always ordered newest first",
		th: "ไม่ใช้เรียงตามในมุมมองตามวัน — วันที่จะเรียงล่าสุดก่อนเสมอ",
		la: "ບໍ່ໃຊ້ຈັດລຳດັບຕາມໃນມຸມມອງຕາມວັນ — ວັນທີຈະຈັດລຽງລ່າສຸດກ່ອນສະເໝີ",
		zh: "按天视图不使用排序方式 — 日期始终按最新排在前面",
	},
};

export default {
	name: "ProductReportPage",

	components: {
		TextSearch,
		TextSelect,
	},

	props: {
		initialView: {
			type: String,
			default: "product",
		},
		startDate: {
			type: String,
			default: "",
		},
		endDate: {
			type: String,
			default: "",
		},
	},

	data: () => ({
		// Product ids whose real item.img URL failed to load (404/broken S3
		// path, etc.) - falls back to the same placeholder as no image at all.
		imgErrorIds: new Set(),
		start_date: last7Start,
		end_date: today,
		search: "",
		loading: false,
		error: false,
		items: [],
		currentPage: 1,
		lastPage: 1,
		total: 0,
		perPage: 15,
		// 'all' is a client-only sentinel meaning "no filter" — never sent to the
		// backend as category_id/size_id when set.
		category: "all",
		unit: "all",
		sort: "quantity_sold_desc",
		activeRange: "7",
		activeView: "product",
		expanded: new Set(),
		// Per-product daily breakdown, loaded lazily on first expand and cached
		// by product id until the filters change (see resetTableState()).
		dailyCache: {},
		// Category/Packaging Unit filter options, loaded once from the project's
		// own real category/product_size list endpoints (never hardcoded), and
		// kept in ENGLISH ONLY regardless of the app's current language — these
		// are real master-data values (categories.la_name/en_name -> en_name;
		// product_size.name/unit), not UI copy, so they must not be translated.
		// The generic "All categories"/"All units" entry IS a UI label, so it is
		// added by the categoryOptions/sizeOptions computed below (via tt()) —
		// never stored here — so it reacts immediately to a language change.
		// A failure here disables just that one select — it never blocks the table.
		categoryOptionsRaw: [],
		categoryOptionsError: false,
		// Packaging Unit is a FIXED type list by explicit product decision — it
		// is intentionally NOT loaded from product_size_paginate (or any other
		// per-product/database source). It never changes with search, date
		// range, page, category, or what units currently exist on real rows.
		// The values below are fixed English words, not real product_size.id
		// numbers. The selected value is sent to the backend as `unit` (see
		// buildParams()), which resolves it against the real product_size
		// table server-side (MyController.php's applyUnitFilter()) — a unit
		// with no matching real product_size row simply returns zero
		// products (the normal empty state), never a validation/API error.
		sizeOptionsRaw: [
			{ text: "piece", value: "piece" },
			{ text: "bottle", value: "bottle" },
			{ text: "can", value: "can" },
			{ text: "pack", value: "pack" },
			{ text: "box", value: "box" },
			{ text: "kg", value: "kg" },
		],
		sizeOptionsError: false,
		// By Day view state — fully isolated from the By Product table's
		// loading/error/items state, per the "By Day error must not break By
		// Product" requirement.
		dayItems: [],
		dayLoading: false,
		dayError: false,
		dayCurrentPage: 1,
		dayLastPage: 1,
		dayTotal: 0,
		dayPerPage: 15,
		// Which date rows are expanded, and each expanded date's lazily-loaded
		// per-product breakdown — same lazy-load/cache pattern as By
		// Product's dailyCache, keyed by date string instead of product id.
		dayExpanded: new Set(),
		dayProductsCache: {},
		// Export button state - the export request runs independently of
		// the table's own loading/error state (buildParams() is reused so
		// the file always matches the current filters, but a failed export
		// must never touch the table or show the blocking global "Failed"
		// modal - see exportReport()).
		exporting: false,
		exportSnackbar: false,
		exportSnackbarText: "",
		// Summary cards are fetched separately from the table (see fetchSummary()).
		// A failure here NEVER touches the table's loading/error state — it only
		// makes the cards fall back to "—" / "N/A", never a fabricated number and
		// never a blocking error.
		summary: {
			loading: false,
			error: false,
			totalQty: 0,
			activeProducts: 0,
			totalProducts: 0,
			totalRevenue: 0,
			avgPerDay: null,
			topSeller: null,
		},
	}),

	async created() {
		if (this.initialView && (this.initialView === "product" || this.initialView === "day")) {
			this.activeView = this.initialView;
		}
		if (this.startDate) {
			this.start_date = this.startDate;
		}
		if (this.endDate) {
			this.end_date = this.endDate;
		}
		if (this.activeView === "day") {
			await Promise.all([
				this.fetchDayItems(1),
				this.fetchSummary(),
				this.fetchCategoryOptions(),
			]);
		} else {
			await Promise.all([
				this.fetchItem(),
				this.fetchSummary(),
				this.fetchCategoryOptions(),
			]);
		}
	},

	computed: {
		kpiItems() {
			const s = this.summary;
			const bad = !!s.error;
			const top = s.topSeller;
			return [
				{
					key: "qty",
					label: this.tt("total_qty_sold"),
					icon: "fal fa-receipt",
					value: bad ? "—" : this.formatNumber(s.totalQty),
					unit: bad ? "" : this.tt("units"),
					note: `${this.daysInRange} ${this.tt("days_in_period")}`,
				},
				{
					key: "active",
					label: this.tt("active_products_sold"),
					icon: "fal fa-box-open",
					value: bad ? "—" : this.formatNumber(s.activeProducts),
					unit: bad ? "" : `/ ${this.formatNumber(s.totalProducts)}`,
					note: this.tt("skus_with_transactions"),
				},
				{
					key: "revenue",
					label: this.tt("total_gross_revenue"),
					icon: "fal fa-coins",
					value: bad ? "—" : `${this.formatNumber(s.totalRevenue)} ₭`,
					note: this.tt("from_completed_credit_sales"),
				},
				{
					key: "avg",
					label: this.tt("avg_qty_per_day"),
					icon: "fal fa-calendar-alt",
					value: bad || s.avgPerDay === null ? "—" : this.formatNumber(s.avgPerDay),
					unit: bad || s.avgPerDay === null ? "" : this.tt("units_per_day"),
					note: this.tt("across_full_filtered_range"),
				},
				{
					key: "top",
					label: this.tt("top_seller_product"),
					icon: "fal fa-crown",
					truncate: true,
					value: bad || !top ? "—" : top.name || "—",
					note:
						bad || !top
							? this.tt("no_sales_in_selected_range")
							: top.quantity_sold !== null
							? `${this.formatNumber(top.quantity_sold)} ${this.tt("sold")}`
							: "",
				},
			];
		},
		rangeLabel() {
			return `${this.toDMY(this.start_date)} → ${this.toDMY(this.end_date)} (${this.daysInRange} days)`;
		},

		daysInRange() {
			const start = new Date(this.start_date + "T00:00:00");
			const end = new Date(this.end_date + "T00:00:00");
			const diff = Math.round((end - start) / 86400000) + 1;
			return Math.max(1, diff);
		},

		maxQtyOnPage() {
			return Math.max(1, ...this.items.map((p) => Number(p.quantity_sold) || 0));
		},

		maxDayQtyOnPage() {
			return Math.max(1, ...this.dayItems.map((d) => Number(d.total_quantity_sold) || 0));
		},

		isFiltered() {
			return (
				Boolean(this.search) ||
				this.category !== "all" ||
				this.unit !== "all" ||
				this.sort !== "quantity_sold_desc" ||
				this.start_date !== last7Start ||
				this.end_date !== today
			);
		},

		// Sorting is now done server-side (see fetchItem()'s `sort` param) over
		// the FULL filtered dataset, not just this page — this is a passthrough,
		// kept as a computed so the template doesn't need to change.
		sortedItems() {
			return this.items;
		},

		// UI copy — reacts to tt()/the current locale, so it re-renders
		// immediately on a language change with no page reload.
		//
		// Uses tt() instead of a bare tt() call: these 4 keys (plus "all_units"
		// below) are new and may not exist yet in the project's real locale
		// files, and tt() on a vue-i18n-style setup echoes the raw key back
		// when a translation is missing. tt() catches exactly that case and
		// falls back to a small built-in dictionary so the dropdown can never
        // show a raw key like "qty_sold_high_to_low" to the user — see tt()
		// in methods for the full explanation. The internal sort `value`s
		// here (quantity_sold_desc/asc, name_asc, revenue_desc) are the exact
		// strings the backend's sort whitelist expects and are untouched.
		sortOptions() {
			return [
				{ value: "quantity_sold_desc", label: this.tt("qty_sold_high_to_low") },
				{ value: "quantity_sold_asc", label: this.tt("qty_sold_low_to_high") },
				{ value: "name_asc", label: this.tt("product_name_a_to_z") },
				{ value: "revenue_desc", label: this.tt("revenue_high_to_low") },
			];
		},

		quickRanges() {
			return [
				{ value: "today", label: this.tt("today") },
				{ value: "yesterday", label: this.tt("yesterday") },
				{ value: "7", label: this.tt("last_7_days") },
				{ value: "30", label: this.tt("last_30_days") },
				{ value: "month", label: this.tt("this_month") },
			];
		},

		// The generic "All categories"/"All units" entry is UI copy and follows
		// the current locale via tt(); every other Category option comes from
		// categoryOptionsRaw (fetched once from the real category master-data
		// endpoint, see fetchCategoryOptions()) and is deliberately NEVER
		// translated. Packaging Unit's own options (sizeOptionsRaw) are now a
		// fixed English list, not fetched — see its definition in data().
		categoryOptions() {
			return [{ text: this.tt("all_categories"), value: "all" }, ...this.categoryOptionsRaw];
		},

		sizeOptions() {
			return [{ text: this.tt("all_units"), value: "all" }, ...this.sizeOptionsRaw];
		},
	},

	watch: {
		initialView(newVal) {
			if (newVal && (newVal === "product" || newVal === "day") && newVal !== this.activeView) {
				this.switchView(newVal);
			}
		},
		startDate(newVal) {
			if (newVal && newVal !== this.start_date) {
				this.start_date = newVal;
			}
		},
		endDate(newVal) {
			if (newVal && newVal !== this.end_date) {
				this.end_date = newVal;
			}
		},
		activeView(newVal) {
			this.$emit("view-change", newVal);
		},
		start_date(newVal) {
			this.$emit("date-change", { startDate: newVal, endDate: this.end_date });
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},
		end_date(newVal) {
			this.$emit("date-change", { startDate: this.start_date, endDate: newVal });
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},
		// TextSelect only emits `input` (via v-model), not a native `change`
		// event, so the filter refresh that used to run from @change="onFilterChange"
		// on the raw v-select now has to hook into the v-model change itself.
		category() {
			this.onFilterChange();
		},
		sort() {
			this.onSortChange();
		},
	},

	methods: {
		formatNumber(n) {
			return Number(n || 0).toLocaleString("en-US");
		},

		// tt(key) — safe drop-in replacement for tt(key), used ONLY for the
		// handful of brand-new keys this fix introduces (the 4 Sort By labels
		// and "all_units"). The project's real tt() reads from its own locale
		// files, which this session cannot edit directly (their file path was
		// never shared). Until those real files are updated with these keys,
		// a vue-i18n-style tt() call on a missing key returns the raw key
		// string itself — which is exactly the "raw i18n key visible to the
		// user" bug this fix must not reproduce.
		//
		// tt() calls the real tt(key) first. If — and only if — that comes
		// back unchanged (the tell-tale sign of a missing key), it falls back
		// to TT_FALLBACK below, keyed by the app's current locale. This means:
		//   - the moment the real locale files gain these keys, tt() starts
		//     returning real translations again and this fallback is simply
		//     never reached — no further code change needed.
		//   - until then, the label is never a raw key, in any of the 4
		//     supported languages.
		// Locale is read the same way this project already reads it
		// elsewhere (falls through $i18n.locale → Vuex store → localStorage
		// → "en"), so this does not introduce a second localization system.
		tt(key) {
			const translated = this.$t(key);
			if (translated !== key) return translated;

			const locale =
				(this.$i18n && this.$i18n.locale) ||
				(this.$store && this.$store.state && this.$store.state.locale) ||
				(typeof localStorage !== "undefined" && localStorage.getItem("locale")) ||
				"en";

			const table = TT_FALLBACK[key];
			if (!table) return translated;
			return table[locale] || table.en || translated;
		},

		// True only if the key is actually present on the row. Distinguishes a
		// real value of 0 from the API never returning that field at all — we
		// must not silently render a missing field as "0" or "—" without a trace.
		hasField(item, key) {
			const present = item && Object.prototype.hasOwnProperty.call(item, key);
			if (!present && !warnedMissingFields.has(key)) {
				warnedMissingFields.add(key);
				// eslint-disable-next-line no-console
				console.warn(
					`[product-report] "${key}" is missing from the /products_report response. Available keys on this row:`,
					item ? Object.keys(item) : item
				);
			}
			return present;
		},

		// hasField() only checks whether the API even returned the key at all
		// (missing key vs. a real 0) — it does NOT check whether the value
		// itself is null/undefined/"" for a key that IS present. That gap let
		// a genuinely-null value (e.g. revenue: null) slip through as if it
		// were a real 0 (Number(null || 0) === 0), and let a null `code`
		// render as a blank chip instead of "—". hasRealValue() closes that
		// gap: present key AND value not null/undefined/"" — so:
		//   quantity_sold: 0        -> real value, shows 0
		//   quantity_sold: null     -> not a real value, shows "—"
		//   quantity_sold: missing  -> not a real value, shows "—" (+ warns)
		hasRealValue(item, key) {
			if (!this.hasField(item, key)) return false;
			const value = item[key];
			return value !== null && value !== undefined && value !== "";
		},

		// A broken/unreachable image URL (e.g. a stale S3 path) falls back to
		// the same placeholder as a genuinely-missing image, tracked per row
		// so one bad image never turns into a broken-image icon.
		productImageUrl(item) {
			if (!item.img || this.imgErrorIds.has(item.id)) return "/images/no_photo.svg";
			if (item.img.startsWith("/")) return item.img;
			if (item.img.includes("http") || item.img.includes("https")) return item.img;
			return `${process.env.VUE_APP_BASE_URL}/uploads/${item.img}`;
		},

		onImgError(item, event) {
			this.imgErrorIds = new Set(this.imgErrorIds).add(item.id);
			if (event && event.target && !event.target.src.endsWith("/images/no_photo.svg")) {
				event.target.src = "/images/no_photo.svg";
			}
		},

		qtyPct(item) {
			return Math.min(100, Math.round(((Number(item.quantity_sold) || 0) / this.maxQtyOnPage) * 100));
		},

		// Real category/unit values only — "—" when the product genuinely has
		// none set (category/size are null from the backend, never fabricated).
		// English only (categories.en_name), for the same reason as the Category
		// filter dropdown — a real master-data value, not UI copy, so it must not
		// mix languages with the rest of the (localized) page.
		categoryLabel(item) {
			if (!item.category) return "—";
			return item.category.en_name || "—";
		},

		unitLabel(item) {
			if (!item.size) return "—";
			return item.size.unit || item.size.name || "—";
		},

		// User-visible date formatting only — DD/MM/YYYY, per the project's
		// display convention. Never used for anything sent back to the API:
		// every param this page sends (start_date/end_date/etc.) still reads
		// straight from this.start_date/this.end_date, which stay in the
		// backend's own YYYY-MM-DD form the whole time — this only reformats
		// an already-ISO date string for on-screen display.
		toDMY(isoDate) {
			if (!isoDate) return "";
			const datePart = String(isoDate).slice(0, 10);
			const [y, m, d] = datePart.split("-");
			if (!y || !m || !d) return isoDate;
			return `${d}/${m}/${y}`;
		},

		// Real datetime string from histories.created_at (via last_sale), or "—"
		// when the product has no sale in the selected range. Displayed as
		// DD/MM/YYYY HH:mm — display format only, never touches the real
		// underlying value.
		formatDate(value) {
			if (!value) return "—";
			const raw = String(value).replace("T", " ");
			const dmy = this.toDMY(raw.slice(0, 10));
			const timePart = raw.slice(11, 16);
			return timePart ? `${dmy} ${timePart}` : dmy;
		},

		toggleExpand(item) {
			const id = item.id;
			const next = new Set(this.expanded);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
				if (!this.dailyCache[id]) {
					this.fetchProductDaily(item);
				}
			}
			this.expanded = next;
		},

		retryDaily(item) {
			this.fetchProductDaily(item);
		},

		// Clears state that only makes sense for the previously-loaded filters —
		// called whenever a filter changes, so a stale expanded row/cached daily
		// breakdown from a different date range/search/category can never be
		// shown under the new one.
		resetTableState() {
			this.currentPage = 1;
			this.expanded = new Set();
			this.dailyCache = {};
		},

		onPerPageChange() {
			this.currentPage = 1;
			this.fetchItem();
		},

		onSortChange() {
			this.currentPage = 1;
			this.fetchItem();
		},

		onFilterChange() {
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},

		goToPage(page) {
			if (page < 1 || page > this.lastPage || page === this.currentPage) return;
			this.currentPage = page;
			this.expanded = new Set();
			this.dailyCache = {};
			this.fetchItem();
		},

		switchView(view) {
			if (this.activeView === view) return;
			this.activeView = view;
			if (view === "day") {
				this.fetchDayItems(1);
			}
		},

		onDayPerPageChange() {
			this.fetchDayItems(1);
		},

		goToDayPage(page) {
			if (page < 1 || page > this.dayLastPage || page === this.dayCurrentPage) return;
			this.fetchDayItems(page);
		},

		buildParams(extra = {}) {
			const params = {
				start_date: this.start_date,
				end_date: this.end_date,
				...extra,
			};
			if (this.search && this.search.trim() !== "") {
				params.q = this.search.trim();
			}
			if (this.category && this.category !== "all") {
				params.category_id = this.category;
			}
			if (this.unit && this.unit !== "all") {
				// Sent as `unit` (a real product_size.unit text value, e.g.
				// "box"), NOT `size_id` — the Packaging Unit dropdown is a
				// fixed English word list (see sizeOptionsRaw in data()), not
				// real product_size.id numbers, so sending it as `size_id`
				// tripped the backend's is_numeric(size_id) validation and
				// aborted the whole request with a 422 ("Unable to load
				// product report") the moment a non-numeric word was
				// selected. The backend now resolves `unit` against the real
				// product_size table itself (see applyUnitFilter() in
				// MyController.php) and returns a normal empty result when
				// nothing matches, instead of erroring.
				params.unit = this.unit;
			}
			return params;
		},

		async exportReport() {
			if (this.exporting) return;
			this.exporting = true;
			try {
				// Same filters as the table (buildParams()) plus the current
				// sort - store_id is never sent from here, the backend takes it
				// from the authenticated JWT exactly like every other Product
				// Report endpoint.
				const params = this.buildParams({ sort: this.sort });

				// skipErrorAlert - a failed export must never open the global
				// blocking "Failed" modal; responseType "blob" so axios hands
				// back the raw xlsx bytes instead of trying to JSON-parse them.
				const response = await this.$http.get("products_report_export", {
					params,
					responseType: "blob",
					skipErrorAlert: true,
				});

				const disposition = response.headers && response.headers["content-disposition"];
				// Fallback filename mirrors the backend's own convention
				// (ExcelController::ProductsReportExport) in case the
				// Content-Disposition header is ever missing - derived from
				// the currently selected date range, never hardcoded.
				let filename =
					this.start_date && this.end_date
						? `product-sales-report-${this.start_date}-to-${this.end_date}.xlsx`
						: "product-sales-report-all-time.xlsx";
				if (disposition) {
					const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
					const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
					if (utf8Match && utf8Match[1]) {
						filename = decodeURIComponent(utf8Match[1]);
					} else if (plainMatch && plainMatch[1]) {
						filename = plainMatch[1].trim();
					}
				}

				const blob = new Blob([response.data], {
					type:
						(response.data && response.data.type) ||
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", filename);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			} catch (err) {
				this.exportSnackbarText = this.tt("unable_to_export_product_sales_report");
				this.exportSnackbar = true;
			} finally {
				this.exporting = false;
			}
		},

		async fetchItem() {
			this.loading = true;
			this.error = false;
			try {
				const params = this.buildParams({
					page: this.currentPage,
					per_page: this.perPage,
					sort: this.sort,
				});

				// skipErrorAlert stops the global axios interceptor from opening the
				// blocking "Failed" modal on error — a Product Report failure must
				// only ever show the inline alert here, and the filters must stay
				// usable while it's shown.
				const { data } = await this.$http.get("products_report", { params, skipErrorAlert: true });

				if (data && data.status) {
					const payload = data.data;
					if (Array.isArray(payload)) {
						this.items = payload;
						this.currentPage = 1;
						this.lastPage = 1;
						this.total = payload.length;
					} else if (payload && Array.isArray(payload.data)) {
						this.items = payload.data;
						this.currentPage = payload.current_page || 1;
						this.lastPage = payload.last_page || 1;
						this.total = payload.total ?? payload.data.length;
					} else {
						this.items = [];
						this.currentPage = 1;
						this.lastPage = 1;
						this.total = 0;
					}
				} else {
					this.error = true;
					this.items = [];
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching product report:", err);
				this.error = true;
				this.items = [];
				// Do NOT retry automatically here — a failed request must not
				// re-trigger itself. The user retries via the inline button, or by
				// changing a filter (which calls fetchItem() exactly once).
			} finally {
				this.loading = false;
			}
		},

		// Isolated from fetchItem()/this.error/this.loading on purpose: a summary
		// failure must never block or blank out the table, and must never open
		// any error dialog. It only ever degrades the 5 cards to "—"/"N/A".
		// Uses the dedicated products_report/summary aggregate endpoint — no
		// more approximating totals from one oversized page of products_report.
		async fetchSummary() {
			this.summary.loading = true;
			this.summary.error = false;
			try {
				const params = this.buildParams({});
				const { data } = await this.$http.get("products_report/summary", { params, skipErrorAlert: true });

				if (data && data.status) {
					const payload = data.data || {};
					this.summary.totalQty = Number(payload.total_quantity_sold ?? 0);
					this.summary.activeProducts = Number(payload.active_products ?? 0);
					this.summary.totalProducts = Number(payload.total_products ?? 0);
					this.summary.totalRevenue = Number(payload.total_revenue ?? 0);
					this.summary.avgPerDay =
						payload.avg_quantity_per_day === null || payload.avg_quantity_per_day === undefined
							? null
							: Number(payload.avg_quantity_per_day);
					// Unlike the aggregate fields above (total_quantity_sold, active_products,
					// etc. — always-computed SQL aggregates that are 0, never null, when
					// there's no data), top_seller's own fields are genuinely optional per
					// the real /products_report/summary response, so `?? 0` here would
					// silently turn a real missing value into a fabricated "0" instead of
					// just omitting that one field. Kept as explicit null when the backend
					// value is null/undefined, so the template can show only the parts that
					// are genuinely there (see the Top Seller card) without inventing data.
					this.summary.topSeller = payload.top_seller
						? {
								name: payload.top_seller.name ?? null,
								code: payload.top_seller.code ?? null,
								quantity_sold:
									payload.top_seller.quantity_sold === null || payload.top_seller.quantity_sold === undefined
										? null
										: Number(payload.top_seller.quantity_sold),
								revenue:
									payload.top_seller.revenue === null || payload.top_seller.revenue === undefined
										? null
										: Number(payload.top_seller.revenue),
						  }
						: null;
				} else {
					this.summary.error = true;
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching product report summary:", err);
				this.summary.error = true;
			} finally {
				this.summary.loading = false;
			}
		},

		// Category/Packaging Unit filter options — loaded once from the project's
		// own real category and product_size list endpoints. Isolated: a failure
		// here only disables that one select (with a tooltip explaining why), it
		// never blocks the table, the summary, or the other filter.
		async fetchCategoryOptions() {
			this.categoryOptionsError = false;
			try {
				const { data } = await this.$http.get("categories_paginate", {
					params: { per_page: 200 },
					skipErrorAlert: true,
				});
				if (data && data.status) {
					const payload = data.data;
					const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
					// English only, deliberately — categories.en_name, never la_name/
					// th_name/cn_name — these are real master-data values shown as
					// filter options, not UI copy, and must never mix languages.
					// A category with no en_name set is left out entirely rather than
					// showing a raw "#71"-style database id as its label — this is a
					// TYPE selector, not a record list, so a record with no real
					// English name is not a valid option here.
					this.categoryOptionsRaw = rows
						.filter((c) => c.en_name && String(c.en_name).trim() !== "")
						.map((c) => ({
							text: c.en_name,
							value: c.id,
						}));
				} else {
					this.categoryOptionsError = true;
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching category filter options:", err);
				this.categoryOptionsError = true;
			}
		},

		// Packaging Unit no longer fetches from product_size_paginate — its
		// options are the fixed sizeOptionsRaw list in data() (see the
		// comment there for why, and the disclosed filtering trade-off).

		// By Day — fully isolated loading/error/items state from the By Product
		// table (see dayLoading/dayError/dayItems above). No sort param: this
		// view has no Sort By control per the current UI.
		async fetchDayItems(page) {
			this.dayLoading = true;
			this.dayError = false;
			// Every fetch here means the visible date rows are about to change
			// (new page, new filters, or a fresh switch into By Day) - any
			// expanded date's cached per-product breakdown could belong to a
			// stale filter set, so it's cleared unconditionally rather than
			// risking a mismatched product list under a date row.
			this.dayExpanded = new Set();
			this.dayProductsCache = {};
			try {
				const params = this.buildParams({ page, per_page: this.dayPerPage });
				const { data } = await this.$http.get("products_report_by_day", { params, skipErrorAlert: true });

				if (data && data.status) {
					const payload = data.data;
					if (Array.isArray(payload)) {
						this.dayItems = payload;
						this.dayCurrentPage = 1;
						this.dayLastPage = 1;
						this.dayTotal = payload.length;
					} else if (payload && Array.isArray(payload.data)) {
						this.dayItems = payload.data;
						this.dayCurrentPage = payload.current_page || 1;
						this.dayLastPage = payload.last_page || 1;
						this.dayTotal = payload.total ?? payload.data.length;
					} else {
						this.dayItems = [];
						this.dayCurrentPage = 1;
						this.dayLastPage = 1;
						this.dayTotal = 0;
					}
				} else {
					this.dayError = true;
					this.dayItems = [];
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching daily sales report:", err);
				this.dayError = true;
				this.dayItems = [];
			} finally {
				this.dayLoading = false;
			}
		},

		// Expand/collapse a By Day date row and lazy-load its per-product
		// breakdown on first expand — same pattern as toggleExpand()/
		// fetchProductDaily() for By Product, keyed by date instead of id.
		toggleDayExpand(row) {
			const date = row.date;
			const next = new Set(this.dayExpanded);
			if (next.has(date)) {
				next.delete(date);
			} else {
				next.add(date);
				if (!this.dayProductsCache[date]) {
					this.fetchDayProducts(row);
				}
			}
			this.dayExpanded = next;
		},

		retryDayProducts(row) {
			this.fetchDayProducts(row);
		},

		// Products sold on one specific date — respects the exact same
		// Category/Packaging Unit/Search filters currently applied to the
		// whole Product Report (via buildParams()), so a date's expanded
		// product list is always scoped identically to the date row's own
		// totals above it. A failure here only affects that one expanded
		// date row, never the rest of the By Day table.
		async fetchDayProducts(row) {
			const date = row.date;
			this.$set(this.dayProductsCache, date, { loading: true, error: false, products: [] });
			try {
				const params = this.buildParams({});
				const { data } = await this.$http.get(`products_report_by_day/${date}/products`, {
					params,
					skipErrorAlert: true,
				});

				if (data && data.status) {
					const payload = data.data || {};
					const products = Array.isArray(payload.products) ? payload.products : [];
					this.$set(this.dayProductsCache, date, { loading: false, error: false, products });
				} else {
					this.$set(this.dayProductsCache, date, { loading: false, error: true, products: [] });
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching products sold on day:", err);
				this.$set(this.dayProductsCache, date, { loading: false, error: true, products: [] });
			}
		},

		// "21/09/2026" — plain dd/mm/yyyy, matching the reference UI's main
		// date-row display (distinct from dailyCardLabel()'s short weekday
		// form used by By Product's per-day timeline cards).
		formatDayDate(dateStr) {
			const d = new Date(dateStr + "T00:00:00");
			if (Number.isNaN(d.getTime())) return dateStr;
			const dd = String(d.getDate()).padStart(2, "0");
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			return `${dd}/${mm}/${d.getFullYear()}`;
		},

		// Full English weekday name for the date-row badge (e.g. "Monday") —
		// deterministic, locale-independent date formatting, not translated
		// business data (same reasoning as dailyCardLabel()).
		dayWeekdayName(dateStr) {
			const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
			const d = new Date(dateStr + "T00:00:00");
			if (Number.isNaN(d.getTime())) return "";
			return WEEKDAYS[d.getDay()];
		},

		// Qty progress-bar fill, relative to the busiest day currently on the
		// page — same idea as qtyPct() for By Product's rows.
		dayQtyPct(row) {
			return Math.min(100, Math.round(((Number(row.total_quantity_sold) || 0) / this.maxDayQtyOnPage) * 100));
		},

		// Per-product daily breakdown for an expanded row — loaded lazily and
		// cached in dailyCache by product id (see toggleExpand()) so reopening
		// the same row under the same filters never refetches. A failure here
		// only affects that one expanded row, never the table.
		async fetchProductDaily(item) {
			const id = item.id;
			this.$set(this.dailyCache, id, { loading: true, error: false, days: [] });
			try {
				// Same start_date/end_date currently applied to the whole Product
				// Report (buildParams() reads this.start_date/this.end_date), so the
				// timeline always matches whatever range/filters are active -- never
				// a hardcoded date, never a fixed number of days.
				const params = this.buildParams({});
				const { data } = await this.$http.get(`products_report/${id}/daily`, {
					params,
					skipErrorAlert: true,
				});

				if (data && data.status) {
					const payload = data.data || {};
					const rawDays = Array.isArray(payload.days) ? payload.days : [];
					// The real endpoint only returns a row for a date that actually had
					// sales (its own documented "absent day = zero" convention -- the
					// same rule reportProductByDay uses). The timeline UI needs every
					// date in the selected range represented, including days with 0
					// sales, so we zero-fill here on the frontend using the exact same
					// start_date/end_date already sent with the request. This is NOT
					// fabricating data: a date with no matching row really did have 0
					// units sold -- we are just making that explicit instead of
					// omitting the date. The real per-day numbers themselves are never
					// altered, only missing dates are added with 0/0.
					const days = this.zeroFillDailyRange(rawDays, this.start_date, this.end_date);
					this.$set(this.dailyCache, id, { loading: false, error: false, days });
				} else {
					this.$set(this.dailyCache, id, { loading: false, error: true, days: [] });
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("Error fetching product daily breakdown:", err);
				this.$set(this.dailyCache, id, { loading: false, error: true, days: [] });
			}
		},

		// Builds the full list of dates from start_date to end_date (inclusive,
		// chronological/ascending, matching the timeline's left-to-right reading
		// order) and merges in the real quantity_sold/revenue for each date the
		// backend actually returned. A date the backend didn't return had 0 real
		// sales per the backend's own documented convention -- shown as 0, never
		// left out, and never a fabricated non-zero value.
		zeroFillDailyRange(rawDays, startDate, endDate) {
			const byDate = new Map();
			for (const d of rawDays) {
				if (d && d.date) byDate.set(String(d.date).slice(0, 10), d);
			}

			const start = new Date(startDate + "T00:00:00");
			const end = new Date(endDate + "T00:00:00");
			if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
				// Malformed/empty range -- fall back to whatever the backend
				// actually returned rather than looping forever or guessing.
				return rawDays.map((d) => ({
					date: d.date,
					quantity_sold: Number(d.quantity_sold ?? 0),
					revenue: Number(d.revenue ?? 0),
				}));
			}

			const result = [];
			for (let cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
				// Built from local getFullYear/getMonth/getDate, NOT
				// toISOString() -- toISOString() converts to UTC first, which
				// silently shifts the date by a day in any timezone behind
				// UTC (e.g. a real sale on 21/09 would land on 20/09's card).
				// That's a real-data misattribution bug, not cosmetic, so it
				// must be avoided here.
				const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(
					cur.getDate()
				).padStart(2, "0")}`;
				const real = byDate.get(iso);
				result.push({
					date: iso,
					quantity_sold: real ? Number(real.quantity_sold ?? 0) : 0,
					revenue: real ? Number(real.revenue ?? 0) : 0,
				});
			}
			return result;
		},

		// "Tue 15/09" -- deterministic English weekday abbreviation + day/month,
		// independent of the browser/app locale, matching the reference UI's
		// timeline card header. This is date formatting, not translated business
		// data, so it stays fixed regardless of the app's current language.
		dailyCardLabel(dateStr) {
			const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
			const d = new Date(dateStr + "T00:00:00");
			if (Number.isNaN(d.getTime())) return dateStr;
			const dd = String(d.getDate()).padStart(2, "0");
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			return `${WEEKDAYS[d.getDay()]} ${dd}/${mm}`;
		},

		onSearch() {
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},

		onClearSearch() {
			this.search = "";
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},

		clearFilter() {
			this.start_date = last7Start;
			this.end_date = today;
			this.search = "";
			this.category = "all";
			this.unit = "all";
			this.activeRange = "7";
			this.sort = "quantity_sold_desc";
			this.resetTableState();
			this.fetchItem();
			this.fetchSummary();
			if (this.activeView === "day") this.fetchDayItems(1);
		},

		applyQuickRange(value) {
			this.activeRange = value;
			if (value === "today") {
				this.start_date = today;
				this.end_date = today;
			} else if (value === "yesterday") {
				const y = addDaysIso(today, -1);
				this.start_date = y;
				this.end_date = y;
			} else if (value === "7") {
				this.start_date = addDaysIso(today, -6);
				this.end_date = today;
			} else if (value === "30") {
				this.start_date = addDaysIso(today, -29);
				this.end_date = today;
			} else if (value === "month") {
				const d = new Date(today + "T00:00:00");
				const first = new Date(d.getFullYear(), d.getMonth(), 1);
				this.start_date = new Date(first.getTime() - first.getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10);
				this.end_date = today;
			}
			// start_date/end_date watchers above already call fetchItem()/fetchSummary()
			// (and fetchDayItems() when relevant) once each — no need to call them
			// again here.
		},
	},
};
</script>

<style scoped>
.br-page {
	--br-accent: var(--v-primary-base, #5b39c9);
	--br-accent-soft: rgba(91, 57, 201, 0.12);
	--br-accent-softer: rgba(91, 57, 201, 0.07);
	--br-accent-border: rgba(91, 57, 201, 0.35);
	--br-border: #e2e8f0;
	--br-muted: #94a3b8;
	--br-text: #141021;
	--br-radius-lg: 16px;
	--br-radius-md: 10px;
	max-width: 100%;
	background: #f6f6fa;
}

.br-page--dark {
	--br-text: #ffffff;
	--br-border: rgba(255, 255, 255, 0.08);
	background: #1b1d30;
}

.br-page--dark .br-panel,
.br-page--dark .br-panel.v-card.v-sheet {
	background: #1e1e2d !important;
	border-color: rgba(255, 255, 255, 0.08) !important;
}

.br-page section {
  width: 100%;
}

/* KPI cards */

/* Main panel */
.br-panel,
.br-panel.v-card.v-sheet {
	background: #ffffff !important;
	border: 1px solid var(--br-border) !important;
	border-radius: 14px !important;
	box-shadow: none !important;
}
.br-panel-header {
	gap: 10px;
}
.br-accent-bar {
	width: 3px;
	height: 15px;
	background: var(--br-accent);
	border-radius: 2px;
}
.br-panel-title {
	margin: 0;
	font-size: 17px;
	font-weight: 500;
	color: var(--br-text);
}
.br-panel-count {
	font-size: 11.5px;
	color: var(--br-muted);
}
.br-tabs {
	display: flex;
	height: 38px;
	box-sizing: border-box;
	align-items: center;
	gap: 4px;
	padding: 3px;
	border-radius: 10px;
	background: #f1f2f7;
	border: 1px solid var(--br-border);
}
.br-tab {
	height: 30px;
	padding: 4px 12px;
	border-radius: 7px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	user-select: none;
	color: var(--br-muted);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s ease;
}
.br-tab--active {
	color: #5b39c9 !important;
	background: #ffffff !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	font-weight: 700;
}
.br-page--dark .br-tab--active {
	color: #c4b5fd !important;
	background: rgba(154, 140, 240, 0.18) !important;
	box-shadow: none;
}

/* Filter row */
.br-filter-row {
	gap: 10px;
}
.br-search {
	flex: 1 1 220px;
	min-width: 210px;
	max-width: 300px;
}
.br-date-range {
	gap: 4px;
}
.br-date-range ::v-deep .v-input__append-inner {
	margin-top: 0 !important;
	margin-bottom: 0 !important;
	align-self: center !important;
	color: #94a3b8 !important;
}
.br-date-range ::v-deep .v-input__append-inner .v-icon {
	color: #94a3b8 !important;
}
.br-date-arrow {
	color: var(--br-muted);
	font-size: 12px;
}
.br-select {
	max-width: 170px;
}
.br-clear-link {
	font-size: 12px;
	color: var(--br-accent);
	cursor: pointer;
	margin-left: 4px;
}
.br-clear-link:hover {
	text-decoration: underline;
}
.br-export-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 13px;
	border-radius: var(--br-radius-md);
	border: 1px solid var(--br-border);
	color: var(--br-muted);
	font-size: 13px;
	cursor: pointer;
	transition: color 0.15s ease, border-color 0.15s ease;
	user-select: none;
}
.br-export-btn:hover {
	color: var(--br-text);
	border-color: #cbd5e1;
}

/* Quick range pills */
.br-quick-bar {
	gap: 8px;
}
.br-quick-chip {
	border: 1px solid transparent;
	background: #f1f5f9;
	color: #475569;
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	padding: 4px 10px;
	border-radius: 9999px;
	cursor: pointer;
	transition: all 0.15s ease;
}
.br-quick-chip:hover {
	background: #e2e8f0;
}
.br-quick-chip--active {
	background: var(--br-accent-soft);
	color: var(--br-accent);
	border-color: var(--br-accent-border);
	font-weight: 600;
}

/* Table styles */
.pr-table-card {
	background: transparent;
	border: none;
	box-shadow: none;
	overflow: hidden;
}
.pr-table-head {
	padding: 12px 6px;
	border-bottom: 1px solid #f1f5f9;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
}
.pr-table-head-left {
	display: flex;
	align-items: center;
	gap: 10px;
}
.pr-table-title {
	font-size: 14px;
	font-weight: 600;
	color: var(--br-text);
	margin: 0;
}
.pr-range-pill {
	font-size: 11px;
	font-weight: 500;
	color: var(--br-muted);
	background: #f1f5f9;
	border-radius: 9999px;
	padding: 2px 8px;
}
.pr-table-hint {
	font-size: 11.5px;
	color: var(--br-muted);
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 6px;
}
.pr-hint-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: var(--br-accent);
	display: inline-block;
}
.pr-skeleton-wrap {
	padding: 16px;
}
.pr-empty {
	padding: 48px 16px;
	text-align: center;
}
.pr-empty-icon {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: #f1f5f9;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 10px;
	font-size: 22px;
}
.pr-empty-title {
	font-size: 14px;
	font-weight: 600;
	color: var(--br-text);
	margin: 0;
}
.pr-empty-text {
	font-size: 12px;
	color: var(--br-muted);
	margin: 4px auto 0;
	max-width: 320px;
}
.pr-empty-btn {
	margin-top: 14px;
	padding: 6px 14px;
	border-radius: 8px;
	font-size: 12px;
	font-weight: 600;
	color: var(--br-accent);
	background: var(--br-accent-soft);
	border: 1px solid var(--br-accent-border);
	cursor: pointer;
}
.pr-table-scroll {
	overflow-x: auto;
}
.pr-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.pr-table thead tr {
	background: #ffffff;
	border-bottom: 1px solid var(--br-border);
}
.pr-table th {
	text-align: left;
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--br-muted);
	padding: 10px 12px;
	white-space: nowrap;
}
.pr-th-icon {
	width: 36px;
	text-align: center !important;
}
.pr-th-num {
	text-align: right !important;
	font-variant-numeric: tabular-nums;
}
.pr-table td {
	padding: 10px 12px;
	vertical-align: middle;
	border-bottom: 1px solid #f1f2f7;
}
.pr-row {
	cursor: pointer;
	transition: background-color 0.12s ease;
}
.pr-row:hover {
	background: var(--br-accent-softer);
}
.pr-row-open {
	background: var(--br-accent-softer);
}
.pr-chevron {
	display: inline-flex;
	width: 22px;
	height: 22px;
	border-radius: 6px;
	background: #f1f5f9;
	color: var(--br-muted);
	align-items: center;
	justify-content: center;
	transition: transform 0.15s ease;
}
.pr-chevron-open {
	transform: rotate(90deg);
	color: var(--br-accent);
}
.pr-prod-cell {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 200px;
}
.pr-avatar {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	overflow: hidden;
	flex: 0 0 34px;
	border: 1px solid var(--br-border);
	background: #f8fafc;
}
.pr-avatar-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}
.pr-prod-name {
	font-weight: 600;
	color: var(--br-text);
	line-height: 1.25;
	font-size: 13px;
}
.pr-prod-sub {
	margin-top: 2px;
}
.pr-code-chip {
	font-family: monospace;
	font-size: 11px;
	background: #f1f5f9;
	color: var(--br-muted);
	padding: 1px 4px;
	border-radius: 4px;
}
.pr-muted-chip {
	display: inline-block;
	font-size: 11px;
	color: var(--br-muted);
	background: #f1f5f9;
	padding: 2px 8px;
	border-radius: 9999px;
}
.pr-muted {
	color: #cbd5e1;
}
.pr-qty-value {
	font-weight: 600;
	color: var(--br-text);
	text-align: right;
	font-variant-numeric: tabular-nums;
}
.pr-qty-bar {
	width: 70px;
	height: 4px;
	background: #f1f5f9;
	border-radius: 9999px;
	overflow: hidden;
	margin: 4px 0 0 auto;
}
.pr-qty-bar-fill {
	height: 100%;
	background: var(--br-accent);
	border-radius: 9999px;
}
.pr-detail-row td {
	background: #fafafc;
	padding: 0;
}
.pr-detail-inner {
	padding: 12px 14px 12px 48px;
	font-size: 12px;
	color: var(--br-muted);
	display: flex;
	align-items: center;
}
.pr-detail-body {
	padding: 10px 14px 12px 48px;
}
.pr-timeline-header {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.03em;
	color: var(--br-muted);
	margin-bottom: 10px;
}
.pr-timeline-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: var(--br-accent);
	flex-shrink: 0;
}
.pr-timeline-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
.pr-timeline-card {
	background: #fff;
	border: 1px solid var(--br-border);
	border-radius: 8px;
	padding: 8px 12px;
	min-width: 88px;
}
.pr-timeline-date {
	font-size: 10.5px;
	color: var(--br-muted);
	margin-bottom: 2px;
}
.pr-timeline-qty {
	font-size: 12.5px;
	font-weight: 600;
	color: var(--br-text);
	margin-bottom: 2px;
	font-variant-numeric: tabular-nums;
}
.pr-timeline-revenue {
	font-size: 11.5px;
	font-weight: 600;
	color: #16a34a;
	font-variant-numeric: tabular-nums;
}
.pr-date-cell {
	display: flex;
	align-items: center;
	gap: 8px;
}
.pr-date-main {
	font-weight: 500;
	color: var(--br-text);
	font-variant-numeric: tabular-nums;
}
.pr-date-weekday {
	font-size: 10.5px;
	font-weight: 600;
	color: var(--br-accent);
	background: var(--br-accent-soft);
	border-radius: 4px;
	padding: 1px 6px;
}
.pr-revenue-green {
	color: #16a34a;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
}
.pr-byday-table {
	width: 100%;
	max-width: 600px;
	border-collapse: collapse;
	font-size: 12px;
}
.pr-byday-table th {
	text-align: left;
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: var(--br-muted);
	padding: 5px 8px;
}
.pr-byday-table td {
	padding: 5px 8px;
	color: var(--br-text);
	border-top: 1px solid var(--br-border);
}
.pr-table-foot {
	padding: 12px 8px;
	border-top: 1px solid var(--br-border);
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	font-size: 12px;
	color: var(--br-muted);
}
.pr-foot-meta {
	font-size: 11.5px;
}
.pr-pager {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-left: auto;
}
.pr-pager-label {
	font-size: 11px;
	color: var(--br-muted);
}
.pr-per-page {
	max-width: 75px;
}
.pr-page-info {
	font-weight: 600;
	color: var(--br-text);
	font-size: 12px;
}
.pr-pager-btn {
	width: 28px;
	height: 28px;
	border-radius: 6px;
	border: 1px solid var(--br-border);
	background: #fff;
	color: var(--br-muted);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: background-color 0.12s ease;
}
.pr-pager-btn:hover:not(:disabled) {
	background: #f1f5f9;
	color: var(--br-text);
}
.pr-pager-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

/* Dark mode overrides */
.br-page--dark .br-quick-chip {
	background: #262638;
	color: #cbd5e1;
	border-color: rgba(255, 255, 255, 0.08);
}
.br-page--dark .br-quick-chip:hover {
	background: rgba(255, 255, 255, 0.08);
	color: #ffffff;
}
.br-page--dark .br-quick-chip--active {
	background: rgba(154, 140, 240, 0.18);
	color: #c4b5fd;
	border-color: rgba(154, 140, 240, 0.4);
}
.br-page--dark .pr-table-head {
	border-bottom-color: rgba(255, 255, 255, 0.08);
}
.br-page--dark .pr-range-pill,
.br-page--dark .pr-code-chip,
.br-page--dark .pr-muted-chip,
.br-page--dark .pr-chevron,
.br-page--dark .pr-empty-icon,
.br-page--dark .pr-qty-bar {
	background: #262638;
	color: #cbd5e1;
}
.br-page--dark .pr-avatar {
	background: #262638;
	border-color: rgba(255, 255, 255, 0.1);
}
.br-page--dark .pr-table thead tr {
	background: transparent;
	border-bottom-color: rgba(255, 255, 255, 0.08);
}
.br-page--dark .pr-table td {
	border-bottom-color: rgba(255, 255, 255, 0.06);
}
.br-page--dark .pr-detail-row td {
	background: #181a2a;
}
.br-page--dark .pr-timeline-card {
	background: #262638;
	border-color: rgba(255, 255, 255, 0.08);
}
.br-page--dark .pr-pager-btn {
	background: #262638;
	border-color: rgba(255, 255, 255, 0.12);
	color: #cbd5e1;
}
.br-page--dark .pr-pager-btn:hover:not(:disabled) {
	background: rgba(154, 140, 240, 0.18);
	color: #ffffff;
}
</style>
